import { useEffect } from "react";
import { AutoComplete, DatePicker, Form, Input, Modal, Segmented, Select } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AUDIT_ORIGINS, AUDIT_TYPES, type AuditDto } from "@audit-plan/shared";
import { api } from "../../api/client";
import { usePeople } from "../../api/people";
import { invalidateAudit, useAuditClients, useSuppliersList } from "../../api/audits";
import { useFactories } from "../../api/factories";
import { useAuth } from "../../auth/AuthContext";
import { useFullModal } from "./mobileModal";
import { useIsMobile } from "../useIsMobile";
import { useNotify } from "./useNotify";

interface FormValues {
  supplierId?: string;
  factoryId?: string;
  type: AuditDto["type"];
  origin: AuditDto["origin"];
  client: string;
  plannedDate: dayjs.Dayjs;
  /** fine della finestra dei semi-annunciati (AUD-F8.4) */
  plannedDateTo?: dayjs.Dayjs | null;
  managerId: string;
  auditorId: string;
  ente: string;
  reviewerId?: string | null;
  participants: string;
  reportTo: string;
  scope: string;
}

/** Nuovo audit, modifica (audit) o follow-up (parent). */
export function AuditFormModal(props: { open: boolean; onClose: () => void; audit?: AuditDto; parent?: AuditDto; supplierId?: string; factoryId?: string; date?: string; onSaved?: (a: AuditDto) => void }) {
  const { open, onClose, audit, parent, supplierId, factoryId, date, onSaved } = props;
  const { t, i18n } = useTranslation();
  const fm = useFullModal();
  const mob = useIsMobile();
  const en = i18n.language === "en";
  const [form] = Form.useForm<FormValues>();
  const qc = useQueryClient();
  const n = useNotify();
  const { me } = useAuth();
  const people = usePeople();
  const suppliers = useSuppliersList();
  const clients = useAuditClients();
  const isNew = !audit;

  useEffect(() => {
    if (!open) return;
    const mgrDefault = audit?.managerId ?? parent?.managerId ?? (me?.role === "manager" ? me.id : people.options(["manager", "admin"])[0]?.value);
    form.resetFields();
    form.setFieldsValue({
      supplierId: audit?.supplierId ?? parent?.supplierId ?? supplierId,
      factoryId: audit?.factoryId ?? factoryId ?? parent?.factoryId,
      type: audit?.type ?? (parent ? "followup" : "periodic"),
      origin: audit?.origin ?? parent?.origin ?? "program",
      client: audit?.client ?? parent?.client ?? "",
      plannedDate: audit?.plannedDate ? dayjs(audit.plannedDate) : date ? dayjs(date) : (undefined as unknown as dayjs.Dayjs),
      plannedDateTo: audit?.plannedDateTo ? dayjs(audit.plannedDateTo) : null,
      managerId: mgrDefault,
      auditorId: audit?.auditorId ?? parent?.auditorId,
      ente: audit?.ente ?? parent?.ente ?? "Brandart - Audit interno",
      reviewerId: audit?.reviewerId ?? parent?.reviewerId ?? mgrDefault,
      // sensitive-ok: valori del form (precompilazione o invio), i campi sono marcati
      participants: audit?.participants ?? "",
      // sensitive-ok: valori del form (precompilazione o invio), i campi sono marcati
      reportTo: audit?.reportTo ?? parent?.reportTo ?? "",
      scope: audit?.scope ?? (parent ? `${en ? "Follow-up of " : "Follow-up dell'audit "}${parent.code}` : ""),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, audit?.id, parent?.id, supplierId, date, people.list.length]);

  const save = useMutation({
    mutationFn: async (v: FormValues) => {
      const body = {
        ...(v.factoryId ? { factoryId: v.factoryId } : {}),
        type: v.type,
        origin: v.origin,
        client: v.origin === "client" ? (v.client ?? "").trim() : "",
        plannedDate: v.plannedDate.format("YYYY-MM-DD"),
        plannedDateTo: v.type === "semi_announced" && v.plannedDateTo ? v.plannedDateTo.format("YYYY-MM-DD") : null,
        managerId: v.managerId,
        auditorId: v.auditorId,
        reviewerId: v.reviewerId || null,
        ente: v.ente ?? "",
        // sensitive-ok: invio del form
        participants: v.participants ?? "",
        // sensitive-ok: invio del form
        reportTo: v.reportTo ?? "",
        scope: v.scope ?? "",
      };
      if (audit) return api.patch<AuditDto>(`/audits/${audit.id}`, body);
      return api.post<AuditDto>("/audits", { ...body, supplierId: v.supplierId ?? parent?.supplierId, parentAuditId: parent?.id ?? null });
    },
    onSuccess: (a) => {
      n.ok();
      invalidateAudit(qc, a?.id ?? audit?.id);
      onSaved?.(a);
      onClose();
    },
    onError: n.fail,
  });

  const title = audit ? `${t("edit")} ${audit.code}` : parent ? `${t("newFollowup")} · ${parent.code}` : t("newAudit");
  const supplierOptions = (suppliers.data ?? [])
    .filter((s) => !s.phaseOut || s.id === (audit?.supplierId ?? parent?.supplierId ?? supplierId))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((s) => ({ value: s.id, label: `${s.name} · ${s.code}` }));
  const type = Form.useWatch("type", form);
  const selSupplier = Form.useWatch("supplierId", form) ?? audit?.supplierId ?? parent?.supplierId ?? supplierId;
  const factories = useFactories({ supplierId: selSupplier, includeInactive: true }, open && !!selSupplier);
  const factoryOptions = (factories.data ?? []).map((f) => ({ value: f.id, label: `${f.name}${f.code ? ` · ${f.code}` : ""}${f.country ? ` (${f.country})` : ""}${f.isSeat ? ` · ${t("f_seat")}` : ""}` }));
  const editableFactory = isNew || audit?.status === "planned" || audit?.status === "confirmed";
  // cambio fornitore: lo stabilimento torna alla sede del nuovo fornitore
  useEffect(() => {
    if (!open || !factories.data) return;
    const cur = form.getFieldValue("factoryId") as string | undefined;
    // default: la sede, oppure l'unico stabilimento; con più stabilimenti e nessuna sede si sceglie a mano
    if (!cur || !factories.data.some((f) => f.id === cur)) form.setFieldValue("factoryId", factories.data.find((f) => f.isSeat)?.id ?? (factories.data.length === 1 ? factories.data[0]!.id : undefined));
  }, [open, factories.data, form]);
  const origin = Form.useWatch("origin", form);
  const req = { required: true, message: t("required") };

  return (
    <Modal {...fm} open={open} title={title} onCancel={onClose} okText={t("save")} cancelText={t("cancel")} confirmLoading={save.isPending} onOk={() => form.validateFields().then((v) => save.mutate(v))} {...(mob ? {} : { width: 720 })} destroyOnHidden>
      <Form form={form} layout="vertical" requiredMark>
        <div style={{ display: "grid", gridTemplateColumns: mob ? "1fr" : "1fr 1fr", gap: "0 16px" }}>
          <Form.Item name="supplierId" label={t("supplier")} rules={isNew && !parent ? [req] : []} style={{ gridColumn: "1 / -1" }}>
            <Select options={supplierOptions} showSearch optionFilterProp="label" disabled={!isNew || !!parent} loading={suppliers.isLoading} />
          </Form.Item>
          <Form.Item name="factoryId" label={t("f_factory")} rules={[req]} style={{ gridColumn: "1 / -1" }} extra={factoryOptions.length > 1 ? t("f_pickHelp") : undefined}>
            <Select options={factoryOptions} showSearch optionFilterProp="label" disabled={!selSupplier || !editableFactory} loading={factories.isLoading} />
          </Form.Item>
          <Form.Item name="type" label={t("auditType")}>
            <Select options={AUDIT_TYPES.map((x) => ({ value: x, label: t(`t_${x}`) }))} />
          </Form.Item>
          <Form.Item name="origin" label={t("c_origin")}>
            <Segmented block options={AUDIT_ORIGINS.map((x) => ({ value: x, label: t(`c_origin_${x}`) }))} />
          </Form.Item>
          {origin === "client" && (
            <Form.Item
              name="client"
              label={t("c_client")}
              style={{ gridColumn: "1 / -1" }}
              rules={[{ validator: (_, v: string) => ((v ?? "").trim() ? Promise.resolve() : Promise.reject(new Error(t("c_clientReq")))) }]}
            >
              <AutoComplete options={clients.map((c) => ({ value: c }))} placeholder={t("c_clientPh")} filterOption={(input, o) => (o?.value ?? "").toLowerCase().includes(input.toLowerCase())} maxLength={120} />
            </Form.Item>
          )}
          {type === "semi_announced" ? (
            <>
              {/* semi-annunciato (AUD-F8.4): finestra fra due date, il fornitore non conosce il giorno */}
              <Form.Item name="plannedDate" label={t("sa_from")} rules={[req]}>
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
              <Form.Item
                name="plannedDateTo"
                label={t("sa_to")}
                dependencies={["plannedDate"]}
                extra={t("sa_windowHint")}
                rules={[
                  { required: true, message: t("sa_needTo") },
                  ({ getFieldValue }) => ({
                    validator: (_, v: dayjs.Dayjs | null) => {
                      const from = getFieldValue("plannedDate") as dayjs.Dayjs | undefined;
                      return !v || !from || !v.isBefore(from, "day") ? Promise.resolve() : Promise.reject(new Error(t("sa_badTo")));
                    },
                  }),
                ]}
              >
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </>
          ) : (
            <Form.Item
              name="plannedDate"
              label={t("plannedDate")}
              rules={[req]}
              extra={type === "unannounced" ? (en ? "Single date, also for unannounced audits (not shown to the supplier)" : "Data singola, anche per i non annunciati (non visibile al fornitore)") : undefined}
            >
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
          )}
          <Form.Item name="managerId" label={t("manager")} rules={[req]}>
            <Select options={people.options(["manager", "admin"])} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="auditorId" label={t("auditor")} rules={[req]}>
            <Select options={people.options(["auditor", "manager"])} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="ente" label={t("ente")}>
            <Input />
          </Form.Item>
          <Form.Item name="reviewerId" label={t("reviewer")}>
            <Select options={people.options(["manager", "admin"])} allowClear showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="participants" label={t("participants")} style={{ gridColumn: "1 / -1" }}>
            <Input.TextArea data-sensitive rows={3} placeholder={en ? "One per line: name - role" : "Uno per riga: nome - ruolo"} />
          </Form.Item>
          <Form.Item name="reportTo" label={t("reportTo")} style={{ gridColumn: "1 / -1" }}>
            <Input data-sensitive />
          </Form.Item>
          <Form.Item name="scope" label={t("scope")} style={{ gridColumn: "1 / -1" }}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}
